import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LOADING_STATE } from "src/types/constants";
import AuditWorkflowService, {
  ExportByIdsRequest,
  ExportByIdsResponse,
} from "src/services/auditWorkflowServeice";
import { AuditJob } from "./auditDataUploadSlice";
import { createFileUrl, downloadFile } from "src/utils/fileUtil";
import { compareStringsIgnoreCase } from "src/utils/stringUtil";
import {
  MetricsRequest,
  MetricsResponse,
} from "src/components/pages/audit/auditDashboard/metricsTypes";

export enum AuditStatus {
  REVIEWED = "Reviewed",
  UNREVIEWED = "Unreviewed",
  MATCHED = "Matched",
}

export enum ReviewStatus {
  YES = "YES",
  NO = "NO",
}

export const NO_OBJECT_INVOLVED = "No object involved";

export const MULTI_OPTION_DELIMITER = "|";

export enum AuditResult {
  LLM_CORRECT = "LLM is Correct",
  HUMAN_CORRECT = "Human is correct",
  INADEQUATE = "Inadequate Information",
  NEITHER = "Neither is correct",
  BOTH_CORRECT = "Both are correct",
  BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT = "Both Austin and EHS Smart Assistant are correct",
  EHS_SMART_ASSISTANT_CORRECT = "EHS Smart Assistant is correct",
}

export type AuditField = {
  fieldName: string;
  austinAnswer: string;
  llmClarification: string;
  llmAnswer: string;
  confirmedAnswer: string | string[];
  isHierarchicalMatched: boolean; // deprecated field after adding both are correct in audit workflow
  auditResult?: AuditResult;
};

export type AuditHistoryEntry = {
  auditFields: AuditField[];
  timestamp: string;
  reviewer?: string;
};

export type AuditData = {
  incidentId: string;
  caseId: string;
  modelId: string;
  incidentDescription: string;
  auditFields: AuditField[];
  auditWorkflowId: string;
  generatedField: number;
  timestamp: string;
  status: AuditStatus;
  reviewer: string;
  events: Record<string, any>[];
  currentEvents: Record<string, any>[];
  smartAssistantData?: SmartAssistantData;
  auditHistory?: AuditHistoryEntry[];
};

export type SmartAssistantFieldData = {
  fieldName: string;
  suggestedValues: string[];
};

export type SmartAssistantData = {
  incidentId: string;
  fieldData: SmartAssistantFieldData[];
  version: number;
};

export type AuditDataView = {
  caseId: string;
  incidentId: string;
  generatedField: number;
  status: AuditStatus;
  reviewer: string;
};

export type AuditState = {
  selectedAuditJob: AuditJob;
  auditListIndex: number;
  auditDataListView: AuditDataView[];
  auditDataTotalCount: number;
  auditDataReviewedCount: number;
  selectedAuditData: AuditData;
  smartAssistantData: SmartAssistantData | null;
  filteredAuditData: Record<string, any>[];
  metricsData: MetricsResponse | null;
  itemsPerPage: number;
  currentPage: number;
  auditDataSequentialId: number;
  auditDataFieldIndex: number;
  isBlind: boolean;
  getAuditJobLoading: LOADING_STATE;
  getListViewLoading: LOADING_STATE;
  getAuditDataLoading: LOADING_STATE;
  getAuditDataStatistics: LOADING_STATE;
  updateAuditDataLoading: LOADING_STATE;
  getSmartAssistantDataLoading: LOADING_STATE;
  getFilteredAuditDataLoading: LOADING_STATE;
  getMetricsLoading: LOADING_STATE;
  exportFilteredAuditDataByIdsLoading: LOADING_STATE;
  exportFilteredAuditDataLoading: LOADING_STATE;
};

export const InitialAuditJob: AuditJob = {
  id: "",
  modelId: "",
  modelName: "",
  ownerAlias: "",
  s3OutputFilePath: "",
  inputFileName: "",
  failedIncidentsS3FilePath: "",
  status: "",
  timestamp: "",
  linesProcessed: 0,
  linesFailed: 0,
  reviewed_count: 0,
  unreviewed_count: 0,
};

export const initialAuditData: AuditData = {
  incidentId: "",
  caseId: "",
  modelId: "",
  incidentDescription: "",
  auditFields: [],
  auditWorkflowId: "",
  generatedField: 0,
  timestamp: "",
  status: AuditStatus.UNREVIEWED,
  reviewer: "",
  events: [],
  currentEvents: [],
};

const initialState: AuditState = {
  selectedAuditJob: InitialAuditJob,
  auditListIndex: -1,
  auditDataListView: [],
  auditDataTotalCount: 0,
  auditDataReviewedCount: 0,
  selectedAuditData: initialAuditData,
  smartAssistantData: null,
  filteredAuditData: [],
  metricsData: null,
  itemsPerPage: 50,
  currentPage: 1,
  auditDataSequentialId: -1,
  auditDataFieldIndex: -1,
  isBlind: false,
  getAuditJobLoading: "idle",
  getListViewLoading: "idle",
  getAuditDataLoading: "idle",
  updateAuditDataLoading: "idle",
  getAuditDataStatistics: "idle",
  getSmartAssistantDataLoading: "idle",
  getFilteredAuditDataLoading: "idle",
  getMetricsLoading: "idle",
  exportFilteredAuditDataByIdsLoading: "idle",
  exportFilteredAuditDataLoading: "idle",
};

export const getAuditDataListView = createAsyncThunk(
  "audit/getAuditDataListView",
  async (auditWorkflowId: string) => {
    const { data } = await AuditWorkflowService.getAuditDataListView(auditWorkflowId);
    return data;
  },
);

export const getAuditJobById = createAsyncThunk(
  "audit/getAuditJobById",
  async (auditWorkflowId: string) => {
    const { data } = await AuditWorkflowService.getAuditJobById(auditWorkflowId);
    return data;
  },
);

export const getAuditData = createAsyncThunk(
  "audit/getAuditData",
  async (payload: { auditWorkflowId: string; incidentId: string }) => {
    const { data } = await AuditWorkflowService.getAuditData(payload);
    return data;
  },
);

export const getSmartAssistantData = createAsyncThunk(
  "audit/getSmartAssistantData",
  async (incidentId: string) => {
    const { data } = await AuditWorkflowService.getSmartAssistantData(incidentId);
    return data;
  },
);

export const getFilteredAuditData = createAsyncThunk(
  "audit/getFilteredAuditData",
  async (payload: { filters: any[]; orderByClause?: string; limit?: number; offset?: number }) => {
    const { data } = await AuditWorkflowService.getFilteredAuditData(
      payload.filters,
      payload.orderByClause,
      payload.limit,
      payload.offset,
    );
    return data;
  },
);

export const getMetrics = createAsyncThunk("audit/getMetrics", async (request: MetricsRequest) => {
  const data = await AuditWorkflowService.getMetrics(request);
  return data;
});

export const getReviewedCaseCSV = createAsyncThunk(
  "audit/getReviewedCaseCSV",
  async (payload: { auditWorkflowId: string; modelId: string }) => {
    const { data } = await AuditWorkflowService.getReviewedCasesCSV(payload);
    return data;
  },
);

export const exportFilteredAuditDataByIds = createAsyncThunk(
  "audit/exportFilteredAuditDataByIds",
  async (request: ExportByIdsRequest): Promise<ExportByIdsResponse> => {
    // Service layer handles the toast notification
    const response = await AuditWorkflowService.exportFilteredAuditDataByIds(request);
    return response;
  },
);

export const exportFilteredAuditData = createAsyncThunk(
  "audit/exportFilteredAuditData",
  async (filters: any[]) => {
    // Service layer handles the toast notification
    const response = await AuditWorkflowService.exportFilteredAuditData(filters);
    return response.data;
  },
);

const appendHistoryToAuditData = (payload: {
  auditData: AuditData;
  reviewer: string;
}): AuditData => {
  const newAuditHistoryEntry: AuditHistoryEntry = {
    auditFields: payload.auditData.auditFields.map((field) => toAuditFields(field)),
    timestamp: new Date().toISOString(),
    reviewer: payload.reviewer,
  };

  const currentAuditHistory = payload.auditData.auditHistory || [];

  return {
    ...payload.auditData,
    auditHistory: [...currentAuditHistory, newAuditHistoryEntry],
  };
};

export const updateAuditData = createAsyncThunk(
  "audit/updateAuditData",
  async (payload: { auditData: AuditData; reviewer: string }) => {
    const currentEvents = payload.auditData.currentEvents || [];
    const latestCurrentEvents = new Map();

    currentEvents.forEach((event) => {
      let key;
      if (event.action === "field_confirmed") {
        key = `${event.action}_${event.fieldName}_${event.auditor}`;
      } else if (event.action === "audit_start" || event.action === "audit_finish") {
        key = `${event.action}_${event.auditor}`;
      } else {
        key = `${event.action}_${event.timestamp}`; // Keep all other events
      }

      if (
        !latestCurrentEvents.has(key) ||
        event.timestamp > latestCurrentEvents.get(key).timestamp
      ) {
        latestCurrentEvents.set(key, event);
      }
    });

    const filteredCurrentEvents = Array.from(latestCurrentEvents.values()).sort((a, b) =>
      a.timestamp.localeCompare(b.timestamp),
    );
    const finishEvent = {
      action: "audit_finish",
      timestamp: new Date().toISOString(),
      auditor: payload.reviewer,
    };

    // Add audit history entry before updating
    const auditDataWithNewHistory = appendHistoryToAuditData(payload);

    const updatedPayload = {
      ...auditDataWithNewHistory,
      auditFields: auditDataWithNewHistory.auditFields.map((auditField: AuditField) =>
        toAuditFields(auditField),
      ),
      reviewer: payload.reviewer,
      events: [...(payload.auditData.events || []), ...filteredCurrentEvents, finishEvent],
    };
    const { data } = await AuditWorkflowService.updateAuditData(updatedPayload);
    return data;
  },
);

export const toAuditFields = (item: AuditField) => {
  return {
    ...item,
    confirmedAnswer:
      typeof item.confirmedAnswer === "string"
        ? item.confirmedAnswer || NO_OBJECT_INVOLVED
        : item.confirmedAnswer.join(MULTI_OPTION_DELIMITER),
  };
};

/** Data Extraction Page Slice */
const { reducer, actions } = createSlice({
  name: "AuditListViewPageSlice",
  initialState,
  reducers: {
    setCurrentPage: (state, { payload }) => {
      state.currentPage = payload;
    },
    setItemPerPage: (state, { payload }) => {
      state.itemsPerPage = payload;
    },
    setListViewIndex: (state, { payload }) => {
      state.auditDataFieldIndex = payload;
    },
    setFieldConfirmedAnswer: (state, { payload }) => {
      const field = state.selectedAuditData.auditFields[payload.fieldIndex];
      field.auditResult = payload.auditResult;
      const smartAssistantField = state.selectedAuditData.smartAssistantData?.fieldData.find(
        (f) => f.fieldName === field.fieldName,
      );
      if (
        payload.auditResult === AuditResult.BOTH_CORRECT ||
        payload.auditResult === AuditResult.BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT ||
        payload.auditResult === AuditResult.HUMAN_CORRECT
      ) {
        field.confirmedAnswer = field.austinAnswer;
      } else if (payload.auditResult === AuditResult.INADEQUATE) {
        field.confirmedAnswer = AuditResult.INADEQUATE;
      } else if (payload.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT) {
        field.confirmedAnswer = smartAssistantField ? smartAssistantField.suggestedValues : "";
      } else if (payload.auditResult === AuditResult.LLM_CORRECT) {
        field.confirmedAnswer = field.llmAnswer;
      } else {
        // when select neither is correct, user could re select LLM / EHS / Inadequate / AUSTIN answer
        // need to compare based on payload.confirmedAnswer
        if (Array.isArray(payload.confirmedAnswer)) {
          payload.confirmedAnswer = payload.confirmedAnswer
            .filter((item: string) => item && item.trim() !== "")
            .join("|");
        }
        // Check which answers match the confirmed answer
        const ehsMatches =
          smartAssistantField &&
          compareStringsIgnoreCase(
            payload.confirmedAnswer,
            smartAssistantField.suggestedValues.join("|"),
          );
        const llmMatches =
          !smartAssistantField &&
          field.llmAnswer &&
          compareStringsIgnoreCase(payload.confirmedAnswer, field.llmAnswer);
        const austinMatches =
          field.austinAnswer &&
          compareStringsIgnoreCase(payload.confirmedAnswer, field.austinAnswer);
        const isInadequate = compareStringsIgnoreCase(
          payload.confirmedAnswer,
          AuditResult.INADEQUATE,
        );

        // Determine audit result based on matches
        if (isInadequate) {
          field.auditResult = AuditResult.INADEQUATE;
        } else if (ehsMatches && austinMatches) {
          field.auditResult = AuditResult.BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT;
        } else if (llmMatches && austinMatches) {
          field.auditResult = AuditResult.BOTH_CORRECT;
        } else if (ehsMatches) {
          field.auditResult = AuditResult.EHS_SMART_ASSISTANT_CORRECT;
        } else if (llmMatches) {
          field.auditResult = AuditResult.LLM_CORRECT;
        } else if (austinMatches) {
          field.auditResult = AuditResult.HUMAN_CORRECT;
        }
        field.confirmedAnswer = payload.confirmedAnswer;
      }
      state.selectedAuditData.currentEvents.push({
        action: "field_confirmed",
        fieldName: field.fieldName,
        value: Array.isArray(payload.confirmedAnswer)
          ? payload.confirmedAnswer.join(MULTI_OPTION_DELIMITER)
          : payload.confirmedAnswer,
        timestamp: new Date().toISOString(),
        auditor: payload.alias,
      });
    },
    addEvent: (state, { payload }) => {
      state.selectedAuditData.currentEvents.push(payload);
    },
    resetListViewPage: (state) => {
      state.getAuditDataLoading = "idle";
      state.updateAuditDataLoading = "idle";
      state.getAuditDataStatistics = "idle";
      state.getAuditJobLoading = "idle";
      state.getListViewLoading = "idle";
    },
    resetAuditReviewPage: (state) => {
      state.getAuditDataLoading = "idle";
      state.updateAuditDataLoading = "idle";
      state.getAuditDataStatistics = "idle";
      state.getAuditJobLoading = "idle";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAuditDataListView.pending, (state) => {
      state.getListViewLoading = "pending";
    });
    builder.addCase(getAuditDataListView.fulfilled, (state, { payload }) => {
      state.getListViewLoading = "fulfilled";
      state.auditDataListView = payload.sort((a: AuditDataView, b: AuditDataView) => {
        // Sort by status (unreview first, then base on case id)
        return a.status === b.status
          ? a.caseId.localeCompare(b.caseId)
          : a.status === AuditStatus.UNREVIEWED
            ? -1
            : 1;
      });
    });
    builder.addCase(getAuditDataListView.rejected, (state) => {
      state.getListViewLoading = "rejected";
    });

    builder.addCase(updateAuditData.pending, (state) => {
      state.updateAuditDataLoading = "pending";
    });
    builder.addCase(updateAuditData.fulfilled, (state, { payload }) => {
      state.updateAuditDataLoading = "fulfilled";
      const index = state.auditDataListView.findIndex(
        (auditDataView: AuditDataView) => auditDataView.incidentId === payload.incidentId,
      );
      if (index !== -1) {
        state.auditDataListView[index].status = payload.status;
      }
    });
    builder.addCase(updateAuditData.rejected, (state) => {
      state.updateAuditDataLoading = "rejected";
    });

    builder.addCase(getAuditData.pending, (state) => {
      state.getAuditDataLoading = "pending";
    });
    builder.addCase(getAuditData.fulfilled, (state, { payload }) => {
      state.getAuditDataLoading = "fulfilled";
      state.selectedAuditData = payload;
      if (!state.selectedAuditData.events) {
        state.selectedAuditData.events = [];
      }
      if (!state.selectedAuditData.currentEvents) {
        state.selectedAuditData.currentEvents = [];
      }
      // Sort smartAssistantData suggestedValues
      if (state.selectedAuditData.smartAssistantData?.fieldData) {
        state.selectedAuditData.smartAssistantData.fieldData.forEach((field) => {
          if (field.suggestedValues) {
            field.suggestedValues.sort();
          }
        });
      }
    });
    builder.addCase(getAuditData.rejected, (state) => {
      state.getAuditDataLoading = "rejected";
    });
    builder.addCase(getAuditJobById.pending, (state, { payload }) => {
      state.getAuditJobLoading = "pending";
    });
    builder.addCase(getAuditJobById.rejected, (state, { payload }) => {
      state.getAuditJobLoading = "rejected";
    });
    builder.addCase(getAuditJobById.fulfilled, (state, { payload }) => {
      state.selectedAuditJob = payload;
      state.getAuditJobLoading = "fulfilled";
    });
    builder.addCase(getReviewedCaseCSV.fulfilled, (state, { payload }) => {
      if (payload) {
        // download file
        downloadFile(createFileUrl(payload, "text/csv"), "reviewed_case.csv");
      }
    });
    builder.addCase(getSmartAssistantData.pending, (state) => {
      state.getSmartAssistantDataLoading = "pending";
    });
    builder.addCase(getSmartAssistantData.fulfilled, (state, { payload }) => {
      state.smartAssistantData = payload;
      state.getSmartAssistantDataLoading = "fulfilled";
    });
    builder.addCase(getSmartAssistantData.rejected, (state) => {
      state.getSmartAssistantDataLoading = "rejected";
    });

    builder.addCase(getFilteredAuditData.pending, (state) => {
      state.getFilteredAuditDataLoading = "pending";
    });
    builder.addCase(getFilteredAuditData.fulfilled, (state, { payload }) => {
      state.filteredAuditData = payload;
      state.getFilteredAuditDataLoading = "fulfilled";
    });
    builder.addCase(getFilteredAuditData.rejected, (state) => {
      state.getFilteredAuditDataLoading = "rejected";
    });

    builder.addCase(getMetrics.pending, (state) => {
      state.getMetricsLoading = "pending";
    });
    builder.addCase(getMetrics.fulfilled, (state, { payload }) => {
      state.metricsData = payload;
      state.getMetricsLoading = "fulfilled";
    });
    builder.addCase(getMetrics.rejected, (state) => {
      state.getMetricsLoading = "rejected";
    });

    builder.addCase(exportFilteredAuditDataByIds.pending, (state) => {
      state.exportFilteredAuditDataByIdsLoading = "pending";
    });
    builder.addCase(exportFilteredAuditDataByIds.fulfilled, (state, { payload }) => {
      state.exportFilteredAuditDataByIdsLoading = "fulfilled";
    });
    builder.addCase(exportFilteredAuditDataByIds.rejected, (state) => {
      state.exportFilteredAuditDataByIdsLoading = "rejected";
    });

    builder.addCase(exportFilteredAuditData.pending, (state) => {
      state.exportFilteredAuditDataLoading = "pending";
    });
    builder.addCase(exportFilteredAuditData.fulfilled, (state) => {
      state.exportFilteredAuditDataLoading = "fulfilled";
    });
    builder.addCase(exportFilteredAuditData.rejected, (state) => {
      state.exportFilteredAuditDataLoading = "rejected";
    });
  },
});

export const {
  setFieldConfirmedAnswer,
  setCurrentPage,
  setItemPerPage,
  resetAuditReviewPage,
  resetListViewPage,
  addEvent,
} = actions;

export default reducer;
